// AUTH-02 (#728) — PKCE (RFC 7636) for the native OAuth flow.
//
// The server used to finish native OAuth by redirecting the in-app browser to
// `com.usetenon.app://auth?token=…&refresh_token=…`. On Android that scheme is
// an unverified exported intent-filter, so any installed app could declare it,
// race the VIEW intent, and walk off with a full session.
//
// Now the app generates a random `code_verifier` BEFORE opening the browser and
// sends only its SHA-256 (`code_challenge`) into the flow. The callback carries
// a one-time code; tokens come back over an authenticated POST that must present
// the verifier. An app that hijacks the callback gets a code it cannot spend —
// the verifier never leaves this app's private storage.

const VERIFIER_KEY = 'tenon_oauth_code_verifier'

function base64url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Generate a PKCE code_verifier: 32 random bytes, base64url-encoded to 43 chars
 * (RFC 7636 §4.1 requires 43-128 chars from the unreserved set).
 */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64url(bytes)
}

/** code_challenge = base64url(SHA-256(verifier)) — the S256 method. */
export async function codeChallengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64url(new Uint8Array(digest))
}

/**
 * Persist the verifier across the browser hop.
 *
 * Capacitor Preferences (app-private native storage) rather than sessionStorage:
 * on Android the WebView process can be killed while the Chrome Custom Tab is
 * foreground, which would drop sessionStorage and strand the user at a callback
 * they can no longer exchange. Falls back to sessionStorage off-native.
 */
export async function storeCodeVerifier(verifier: string): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences')
    await Preferences.set({ key: VERIFIER_KEY, value: verifier })
  } catch {
    try { sessionStorage.setItem(VERIFIER_KEY, verifier) } catch { /* ignore */ }
  }
}

/**
 * Read and delete the stored verifier — single-use on the client, mirroring the
 * server's single-use code. Returns null when no flow is pending.
 */
export async function takeCodeVerifier(): Promise<string | null> {
  let value: string | null = null
  try {
    const { Preferences } = await import('@capacitor/preferences')
    value = (await Preferences.get({ key: VERIFIER_KEY })).value
    if (value) await Preferences.remove({ key: VERIFIER_KEY })
  } catch {
    /* fall through to sessionStorage */
  }
  if (!value) {
    try {
      value = sessionStorage.getItem(VERIFIER_KEY)
      if (value) sessionStorage.removeItem(VERIFIER_KEY)
    } catch { /* ignore */ }
  }
  return value
}

/**
 * Start a PKCE flow: mint a verifier, stash it, and return the query-string
 * fragment to append to the OAuth init URL.
 */
export async function beginPKCE(): Promise<string> {
  const verifier = generateCodeVerifier()
  await storeCodeVerifier(verifier)
  const challenge = await codeChallengeFor(verifier)
  return '&code_challenge=' + encodeURIComponent(challenge) + '&code_challenge_method=S256'
}
